import axios from 'axios';
import Vue from 'vue';

const AI_API_BASE_URL = 'https://api-my-oracle.nicesky-271038f1.brazilsouth.azurecontainerapps.io';
const MODULE_NAME = 'MyConfig';

/**
 * Gets both PHP and .NET authentication tokens from cookies / localStorage
 */
function getAuthTokens() {
  let netToken = '';
  let phpToken = '';

  try {
    const userInfo = Vue.prototype.$cookies?.get('userInfo');
    if (userInfo) {
      netToken = userInfo.temporaryToken || '';
      phpToken = userInfo.accessToken || '';
    }
  } catch (e) {
    // ignore
  }

  if (!phpToken) {
    phpToken = localStorage.getItem('accessToken') || '';
  }

  return { netToken, phpToken };
}

/**
 * Gets currently saved conversation ID or null if new conversation
 */
export function getStoredConversationId() {
  return sessionStorage.getItem('ai_conversation_id') || null;
}

/**
 * Saves conversation ID returned from MyOracle backend
 */
export function saveConversationId(convId) {
  if (convId) {
    sessionStorage.setItem('ai_conversation_id', convId);
  }
}

/**
 * Clears current conversation ID to start a fresh chat (sends null on next call)
 */
export function resetConversationId() {
  sessionStorage.removeItem('ai_conversation_id');
}

/**
 * Sends a text message prompt to MyOracle AI (/api/chat)
 */
export async function sendAiPrompt(message) {
  const convId = getStoredConversationId();
  const { netToken, phpToken } = getAuthTokens();

  const payload = {
    message: message,
    modulo: MODULE_NAME,
    conversationId: convId // null on first call
  };

  const headers = {
    'Content-Type': 'application/json'
  };

  // Envia ambos os tokens no cabeçalho
  if (netToken) {
    headers['Authorization'] = `Bearer ${netToken}`;
    headers['X-NET-Token'] = netToken;
  } else if (phpToken) {
    headers['Authorization'] = `Bearer ${phpToken}`;
  }

  if (phpToken) {
    headers['X-PHP-Token'] = phpToken;
  }

  try {
    const response = await axios.post(`${AI_API_BASE_URL}/api/chat`, payload, {
      headers,
      timeout: 30000
    });

    return processBackendResponse(response.data);
  } catch (error) {
    console.error('[MyOracle AI] Erro na requisição /api/chat:', error);
    if (error.response && error.response.data) {
      return processBackendResponse(error.response.data);
    }
    throw error;
  }
}

/**
 * Sends a WAV audio file (multipart/form-data) to MyOracle AI (/api/chat/audio).
 * Expects OpenAI Realtime 2.1 format: WAV PCM, mono, 16-bit, 24 kHz.
 * The file is sent as `File` (not `message`).
 */
export async function sendAiAudio(audioBlob) {
  const convId = getStoredConversationId();
  const { netToken, phpToken } = getAuthTokens();

  const formData = new FormData();
  const wavBlob =
    audioBlob instanceof Blob
      ? audioBlob
      : new Blob([audioBlob], { type: 'audio/wav' });

  formData.append('File', wavBlob, 'audio.wav');
  formData.append('Modulo', MODULE_NAME);

  if (convId) {
    formData.append('ConversationId', convId);
  }

  const headers = {};

  // Envia ambos os tokens no cabeçalho
  if (netToken) {
    headers['Authorization'] = `Bearer ${netToken}`;
    headers['X-NET-Token'] = netToken;
  } else if (phpToken) {
    headers['Authorization'] = `Bearer ${phpToken}`;
  }

  if (phpToken) {
    headers['X-PHP-Token'] = phpToken;
  }

  try {
    const response = await axios.post(`${AI_API_BASE_URL}/api/chat/audio`, formData, {
      headers,
      timeout: 45000
    });

    return processBackendResponse(response.data);
  } catch (error) {
    console.error('[MyOracle AI] Erro na requisição /api/chat/audio:', error);
    if (error.response && error.response.data) {
      return processBackendResponse(error.response.data);
    }
    throw error;
  }
}

/**
 * Extracts conversationId and text response from MyOracle backend
 */
function processBackendResponse(data) {
  if (!data) return 'Sem resposta do servidor de IA.';

  // If object has conversationId, save it for subsequent calls
  if (typeof data === 'object') {
    if (data.conversationId) {
      saveConversationId(data.conversationId);
    }
    if (data.response) return data.response;
    if (data.message) return data.message;
    if (data.reply) return data.reply;
    if (data.text) return data.text;
    if (data.content) return data.content;
  }

  if (typeof data === 'string') return data;

  return JSON.stringify(data);
}
